import { c as _c } from "react/compiler-runtime";
import { getChatSessionsByChatSessionIdOptions } from '@onefootprint/fetch-dashboard';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useChatSessionIsLive } from '@/features/ask-percy';
import useSession from '@/hooks/use-session';
import type { AgentSessionInfo } from '../../hooks/use-agent-session';
import { useChatSession } from '../../hooks/use-chat-session';
import { useChatSessionMessages } from '../../hooks/use-chat-session-messages';
import type { ChatApprovalRequiredEvent, ChatResourceEvent, ChatTurnCompleteEvent } from '../../hooks/use-chat-ws';
import type { StartNewChat } from '../../types';
import { mapChatSessionMessages } from '../../utils/map-chat-session-messages';
import ChatMessagesSkeleton from '../chat-messages-skeleton';
import ChatSession from '../chat-session';
import CostStatusBar from '../cost-status-bar';
export type ChatExperienceProps = {
  chatSessionId: string;
  /**
   * Starts a brand-new chat (the no-session draft input, and the autopilot "new chat from a
   * finished transcript" path). The parent owns creation (it holds `useStartChat` and reports the
   * new id via its own success handler); ChatExperience just wires this onto the session the chat
   * UI calls. Existing sessions resume via `resumeSession`, so it fires only when there's no
   * session to resume.
   */
  startNewSession?: StartNewChat;
  // Externally-owned session (investigation useAgentSession). When set, the internal
  // default-session machinery is suppressed — entityInvestigation can't be kind:'default'.
  agentSession?: AgentSessionInfo;
  onResourceEvent?: (event: ChatResourceEvent) => void;
  /** Called on every websocket `ready` handshake — initial connection and each reconnect. */
  onReady?: () => void;
  onTurnComplete?: (event: ChatTurnCompleteEvent) => void;
  onApprovalRequired?: (event: ChatApprovalRequiredEvent) => void;
  /** Composer placeholder once the conversation is under way; surfaces with their own subject keep their wording. */
  replyPlaceholder?: string;
};
const ChatExperience = t0 => {
  const $ = _c(46);
  const {
    chatSessionId,
    startNewSession,
    agentSession: externalAgentSession,
    onResourceEvent,
    onReady,
    onTurnComplete,
    onApprovalRequired,
    replyPlaceholder
  } = t0;
  const isExternal = !!externalAgentSession;
  const autoResumedRef = useRef(false);
  const [autoResumeFired, setAutoResumeFired] = useState(false);
  const [hasRenderedChat, setHasRenderedChat] = useState(false);
  const {
    isLive: orgIsLive
  } = useSession();
  const internalIsLive = useChatSessionIsLive(chatSessionId);
  const isLive = isExternal ? orgIsLive : internalIsLive;
  const {
    session: internalSession,
    isLoading: isLoadingSession,
    resume,
    clear,
    error: resumeError
  } = useChatSession(isExternal ? undefined : chatSessionId, isLive);
  let t1;
  if ($[0] !== chatSessionId || $[1] !== isLive) {
    t1 = getChatSessionsByChatSessionIdOptions({
      path: {
        chatSessionId
      },
      headers: isLive === undefined ? undefined : {
        "x-is-live": String(isLive)
      }
    });
    $[0] = chatSessionId;
    $[1] = isLive;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isLive !== undefined && !isExternal;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      ...t1,
      enabled: t2,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      placeholderData: _temp
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const checkQuery = useQuery(t3);
  const shouldLoadPersistedMessages = isExternal || checkQuery.data?.isRunning === false;
  const {
    messages: persistedMessages,
    isLoading: isLoadingMessages
  } = useChatSessionMessages(chatSessionId, isLive, shouldLoadPersistedMessages);
  let t4;
  if ($[6] !== persistedMessages) {
    t4 = mapChatSessionMessages(persistedMessages);
    $[6] = persistedMessages;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const initialMessages = t4;
  const t5 = resumeError ?? checkQuery.error;
  let t6;
  if ($[8] !== clear) {
    t6 = () => {
      clear();
    };
    $[8] = clear;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== internalSession || $[11] !== isLoadingSession || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      session: internalSession,
      isLoading: isLoadingSession,
      error: t5,
      handleConnectionLost: t6
    };
    $[10] = internalSession;
    $[11] = isLoadingSession;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const internalAgentSession = t7;
  const agentSession = externalAgentSession ?? internalAgentSession;
  const resumeSession = isExternal ? undefined : resume;
  const session = agentSession.session;
  const isAutopilotEndedConversation = isExternal && !session;
  const isCheckFirstLoad = !isExternal && checkQuery.isPending;
  const shouldAutoResume = !isExternal && checkQuery.data?.isRunning === true;
  const isResolvingHistory = isLoadingMessages || isCheckFirstLoad;
  const isAboutToAutoResume = shouldAutoResume && !autoResumeFired;
  const isLoadingShellRaw = isExternal ? !session && isLoadingMessages : !session && (isResolvingHistory || isLoadingSession || isAboutToAutoResume);
  const isLoadingShell = isLoadingShellRaw && !hasRenderedChat;
  let t8;
  let t9;
  if ($[15] !== isCheckFirstLoad || $[16] !== isExternal || $[17] !== isLoadingMessages || $[18] !== resume || $[19] !== session || $[20] !== shouldAutoResume) {
    t8 = () => {
      if (isExternal) {
        return;
      }
      if (autoResumedRef.current) {
        return;
      }
      if (session) {
        return;
      }
      if (isLoadingMessages || isCheckFirstLoad) {
        return;
      }
      autoResumedRef.current = true;
      setAutoResumeFired(true);
      if (!shouldAutoResume) {
        return;
      }
      resume();
    };
    t9 = [isExternal, session, isLoadingMessages, isCheckFirstLoad, shouldAutoResume, resume];
    $[15] = isCheckFirstLoad;
    $[16] = isExternal;
    $[17] = isLoadingMessages;
    $[18] = resume;
    $[19] = session;
    $[20] = shouldAutoResume;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[23] !== isLoadingShellRaw) {
    t10 = () => {
      if (!isLoadingShellRaw) {
        setHasRenderedChat(true);
      }
    };
    t11 = [isLoadingShellRaw];
    $[23] = isLoadingShellRaw;
    $[24] = t10;
    $[25] = t11;
  } else {
    t10 = $[24];
    t11 = $[25];
  }
  useEffect(t10, t11);
  let t12;
  if ($[26] !== agentSession || $[27] !== chatSessionId || $[28] !== initialMessages || $[29] !== isAutopilotEndedConversation || $[30] !== isLive || $[31] !== isLoadingShell || $[32] !== onApprovalRequired || $[33] !== onReady || $[34] !== onResourceEvent || $[35] !== onTurnComplete || $[36] !== replyPlaceholder || $[37] !== resumeSession || $[38] !== startNewSession) {
    t12 = <div className="absolute inset-0 flex bg-primary overflow-hidden"><div className="flex-1 min-w-0 h-full relative flex flex-col overflow-hidden">{isLoadingShell ? <ChatMessagesSkeleton /> : <ChatSession chatSessionId={chatSessionId} agentSession={agentSession} onStartNewSession={startNewSession} onResumeSession={resumeSession} initialMessages={initialMessages} emptyStateLabel="Start a new chat with Percy" isLive={isLive} isAutopilotEndedConversation={isAutopilotEndedConversation} onResourceEvent={onResourceEvent} onReady={onReady} onTurnComplete={onTurnComplete} onApprovalRequired={onApprovalRequired} replyPlaceholder={replyPlaceholder} />}</div></div>;
    $[26] = agentSession;
    $[27] = chatSessionId;
    $[28] = initialMessages;
    $[29] = isAutopilotEndedConversation;
    $[30] = isLive;
    $[31] = isLoadingShell;
    $[32] = onApprovalRequired;
    $[33] = onReady;
    $[34] = onResourceEvent;
    $[35] = onTurnComplete;
    $[36] = replyPlaceholder;
    $[37] = resumeSession;
    $[38] = startNewSession;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== chatSessionId || $[41] !== isExternal) {
    t13 = !isExternal ? <CostStatusBar chatSessionId={chatSessionId} /> : null;
    $[40] = chatSessionId;
    $[41] = isExternal;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t12 || $[44] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  return t14;
};
export default ChatExperience;
function _temp(prev) {
  return prev;
}